import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  Paper,
  Tabs,
  Tab,
  Alert,
  IconButton,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextareaAutosize,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  Backup as BackupIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

interface SystemConfig {
  siteName: string;
  siteDescription: string;
  siteUrl: string;
  adminEmail: string;
  timezone: string;
  dateFormat: string;
  language: string;
  maintenanceMode: boolean;
  debugMode: boolean;
}

interface SecurityConfig {
  passwordMinLength: number;
  passwordRequireUppercase: boolean;
  passwordRequireLowercase: boolean;
  passwordRequireNumbers: boolean;
  passwordRequireSymbols: boolean;
  sessionTimeout: number;
  maxLoginAttempts: number;
  enableTwoFactor: boolean;
  enableCaptcha: boolean;
  allowedFileTypes: string[];
  maxFileSize: number;
}

interface BackupConfig {
  autoBackup: boolean;
  backupFrequency: 'daily' | 'weekly' | 'monthly';
  backupRetention: number;
  backupLocation: string;
  lastBackup: string;
  nextBackup: string;
}

const SystemSettings: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [systemConfig, setSystemConfig] = useState<SystemConfig>({
    siteName: 'Monio Manufacturing',
    siteDescription: 'Leading African Manufacturing Company',
    siteUrl: 'https://monio.com',
    adminEmail: 'admin@monio.com',
    timezone: 'Africa/Nairobi',
    dateFormat: 'DD/MM/YYYY',
    language: 'en',
    maintenanceMode: false,
    debugMode: false,
  });
  const [securityConfig, setSecurityConfig] = useState<SecurityConfig>({
    passwordMinLength: 8,
    passwordRequireUppercase: true,
    passwordRequireLowercase: true,
    passwordRequireNumbers: true,
    passwordRequireSymbols: false,
    sessionTimeout: 30,
    maxLoginAttempts: 5,
    enableTwoFactor: true,
    enableCaptcha: true,
    allowedFileTypes: ['jpg', 'png', 'pdf', 'doc', 'docx'],
    maxFileSize: 10,
  });
  const [backupConfig, setBackupConfig] = useState<BackupConfig>({
    autoBackup: true,
    backupFrequency: 'daily',
    backupRetention: 30,
    backupLocation: '/backups',
    lastBackup: '',
    nextBackup: '',
  });
  const [systemStatus, setSystemStatus] = useState({
    database: 'healthy',
    storage: 'healthy',
    memory: 'healthy',
    cpu: 'healthy',
  });
  const [isBackupDialogOpen, setIsBackupDialogOpen] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadSystemData();
  }, []);

  const loadSystemData = async () => {
    try {
      // Load system configuration
      const systemResponse = await fetch('/api/admin/system/config');
      if (systemResponse.ok) {
        const systemData = await systemResponse.json();
        setSystemConfig(systemData);
      }

      // Load security configuration
      const securityResponse = await fetch('/api/admin/system/security');
      if (securityResponse.ok) {
        const securityData = await securityResponse.json();
        setSecurityConfig(securityData);
      }

      // Load backup configuration
      const backupResponse = await fetch('/api/admin/system/backup');
      if (backupResponse.ok) {
        const backupData = await backupResponse.json();
        setBackupConfig(backupData);
      }

      // Load system status
      const statusResponse = await fetch('/api/admin/system/status');
      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        setSystemStatus(statusData);
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load system data' });
    }
  };

  const handleSystemConfigSave = async () => {
    try {
      const response = await fetch('/api/admin/system/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(systemConfig),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'System configuration saved successfully' });
      } else {
        setMessage({ type: 'error', text: 'Failed to save system configuration' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error saving system configuration' });
    }
  };

  const handleSecurityConfigSave = async () => {
    try {
      const response = await fetch('/api/admin/system/security', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(securityConfig),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Security configuration saved successfully' });
      } else {
        setMessage({ type: 'error', text: 'Failed to save security configuration' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error saving security configuration' });
    }
  };

  const handleBackupConfigSave = async () => {
    try {
      const response = await fetch('/api/admin/system/backup', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(backupConfig),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Backup configuration saved successfully' });
      } else {
        setMessage({ type: 'error', text: 'Failed to save backup configuration' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error saving backup configuration' });
    }
  };

  const handleManualBackup = async () => {
    try {
      const response = await fetch('/api/admin/system/backup/manual', {
        method: 'POST',
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Manual backup initiated successfully' });
        setIsBackupDialogOpen(false);
      } else {
        setMessage({ type: 'error', text: 'Failed to initiate manual backup' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error initiating manual backup' });
    }
  };

  const handleClearCache = async () => {
    try {
      const response = await fetch('/api/admin/system/cache/clear', {
        method: 'POST',
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Cache cleared successfully' });
      } else {
        setMessage({ type: 'error', text: 'Failed to clear cache' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error clearing cache' });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'success';
      case 'warning': return 'warning';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircleIcon color="success" />;
      case 'warning': return <WarningIcon color="warning" />;
      case 'critical': return <WarningIcon color="error" />;
      default: return <WarningIcon />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', mb: 3 }}>
        <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        System Settings
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab icon={<SettingsIcon />} label="General" />
          <Tab icon={<SecurityIcon />} label="Security" />
          <Tab icon={<BackupIcon />} label="Backup & Maintenance" />
          <Tab icon={<StorageIcon />} label="System Status" />
        </Tabs>
      </Paper>

      {/* General Settings Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  General Settings
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Site Name"
                      value={systemConfig.siteName}
                      onChange={(e) => setSystemConfig({ ...systemConfig, siteName: e.target.value })}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Site URL"
                      value={systemConfig.siteUrl}
                      onChange={(e) => setSystemConfig({ ...systemConfig, siteUrl: e.target.value })}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Site Description"
                      value={systemConfig.siteDescription}
                      onChange={(e) => setSystemConfig({ ...systemConfig, siteDescription: e.target.value })}
                      margin="normal"
                      multiline
                      rows={2}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Admin Email"
                      type="email"
                      value={systemConfig.adminEmail}
                      onChange={(e) => setSystemConfig({ ...systemConfig, adminEmail: e.target.value })}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Timezone</InputLabel>
                      <Select
                        value={systemConfig.timezone}
                        onChange={(e) => setSystemConfig({ ...systemConfig, timezone: e.target.value })}
                        label="Timezone"
                      >
                        <MenuItem value="Africa/Nairobi">Africa/Nairobi</MenuItem>
                        <MenuItem value="UTC">UTC</MenuItem>
                        <MenuItem value="America/New_York">America/New_York</MenuItem>
                        <MenuItem value="Europe/London">Europe/London</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Language</InputLabel>
                      <Select
                        value={systemConfig.language}
                        onChange={(e) => setSystemConfig({ ...systemConfig, language: e.target.value })}
                        label="Language"
                      >
                        <MenuItem value="en">English</MenuItem>
                        <MenuItem value="sw">Swahili</MenuItem>
                        <MenuItem value="fr">French</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={systemConfig.maintenanceMode}
                        onChange={(e) => setSystemConfig({ ...systemConfig, maintenanceMode: e.target.checked })}
                      />
                    }
                    label="Maintenance Mode"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={systemConfig.debugMode}
                        onChange={(e) => setSystemConfig({ ...systemConfig, debugMode: e.target.checked })}
                      />
                    }
                    label="Debug Mode"
                  />
                </Box>
                <Box sx={{ mt: 3 }}>
                  <Button variant="contained" onClick={handleSystemConfigSave}>
                    Save General Settings
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Quick Actions
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={handleClearCache}
                  >
                    Clear Cache
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<BackupIcon />}
                    onClick={() => setIsBackupDialogOpen(true)}
                  >
                    Manual Backup
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Security Settings Tab */}
      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Security Settings
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  Password Policy
                </Typography>
                <TextField
                  fullWidth
                  label="Minimum Password Length"
                  type="number"
                  value={securityConfig.passwordMinLength}
                  onChange={(e) => setSecurityConfig({ ...securityConfig, passwordMinLength: parseInt(e.target.value) })}
                  margin="normal"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={securityConfig.passwordRequireUppercase}
                      onChange={(e) => setSecurityConfig({ ...securityConfig, passwordRequireUppercase: e.target.checked })}
                    />
                  }
                  label="Require Uppercase Letters"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={securityConfig.passwordRequireLowercase}
                      onChange={(e) => setSecurityConfig({ ...securityConfig, passwordRequireLowercase: e.target.checked })}
                    />
                  }
                  label="Require Lowercase Letters"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={securityConfig.passwordRequireNumbers}
                      onChange={(e) => setSecurityConfig({ ...securityConfig, passwordRequireNumbers: e.target.checked })}
                    />
                  }
                  label="Require Numbers"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={securityConfig.passwordRequireSymbols}
                      onChange={(e) => setSecurityConfig({ ...securityConfig, passwordRequireSymbols: e.target.checked })}
                    />
                  }
                  label="Require Special Characters"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  Session & Authentication
                </Typography>
                <TextField
                  fullWidth
                  label="Session Timeout (minutes)"
                  type="number"
                  value={securityConfig.sessionTimeout}
                  onChange={(e) => setSecurityConfig({ ...securityConfig, sessionTimeout: parseInt(e.target.value) })}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Max Login Attempts"
                  type="number"
                  value={securityConfig.maxLoginAttempts}
                  onChange={(e) => setSecurityConfig({ ...securityConfig, maxLoginAttempts: parseInt(e.target.value) })}
                  margin="normal"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={securityConfig.enableTwoFactor}
                      onChange={(e) => setSecurityConfig({ ...securityConfig, enableTwoFactor: e.target.checked })}
                    />
                  }
                  label="Enable Two-Factor Authentication"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={securityConfig.enableCaptcha}
                      onChange={(e) => setSecurityConfig({ ...securityConfig, enableCaptcha: e.target.checked })}
                    />
                  }
                  label="Enable CAPTCHA"
                />
                <TextField
                  fullWidth
                  label="Max File Size (MB)"
                  type="number"
                  value={securityConfig.maxFileSize}
                  onChange={(e) => setSecurityConfig({ ...securityConfig, maxFileSize: parseInt(e.target.value) })}
                  margin="normal"
                />
              </Grid>
            </Grid>
            <Box sx={{ mt: 3 }}>
              <Button variant="contained" onClick={handleSecurityConfigSave}>
                Save Security Settings
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Backup & Maintenance Tab */}
      {activeTab === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Backup Configuration
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={backupConfig.autoBackup}
                          onChange={(e) => setBackupConfig({ ...backupConfig, autoBackup: e.target.checked })}
                        />
                      }
                      label="Enable Auto Backup"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth margin="normal">
                      <InputLabel>Backup Frequency</InputLabel>
                      <Select
                        value={backupConfig.backupFrequency}
                        onChange={(e) => setBackupConfig({ ...backupConfig, backupFrequency: e.target.value as any })}
                        label="Backup Frequency"
                      >
                        <MenuItem value="daily">Daily</MenuItem>
                        <MenuItem value="weekly">Weekly</MenuItem>
                        <MenuItem value="monthly">Monthly</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Backup Retention (days)"
                      type="number"
                      value={backupConfig.backupRetention}
                      onChange={(e) => setBackupConfig({ ...backupConfig, backupRetention: parseInt(e.target.value) })}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Backup Location"
                      value={backupConfig.backupLocation}
                      onChange={(e) => setBackupConfig({ ...backupConfig, backupLocation: e.target.value })}
                      margin="normal"
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 3 }}>
                  <Button variant="contained" onClick={handleBackupConfigSave}>
                    Save Backup Settings
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Backup Status
                </Typography>
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Last Backup
                  </Typography>
                  <Typography variant="h6">
                    {backupConfig.lastBackup ? new Date(backupConfig.lastBackup).toLocaleDateString() : 'Never'}
                  </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Next Backup
                  </Typography>
                  <Typography variant="h6">
                    {backupConfig.nextBackup ? new Date(backupConfig.nextBackup).toLocaleDateString() : 'Not scheduled'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* System Status Tab */}
      {activeTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  System Health
                </Typography>
                <List>
                  {Object.entries(systemStatus).map(([component, status]) => (
                    <ListItem key={component}>
                      <ListItemText
                        primary={component.charAt(0).toUpperCase() + component.slice(1)}
                        secondary={`Status: ${status}`}
                      />
                      <ListItemSecondaryAction>
                        {getStatusIcon(status)}
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  System Information
                </Typography>
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h3" color="primary">
                    v2.1.0
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Current Version
                  </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h3" color="success.main">
                    99.9%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Uptime
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Manual Backup Dialog */}
      <Dialog open={isBackupDialogOpen} onClose={() => setIsBackupDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Manual Backup</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will create a complete backup of the system including database and files.
            The process may take several minutes.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsBackupDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleManualBackup} variant="contained">
            Start Backup
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SystemSettings; 